const DEFAULT_VOLUME = 0.18;

const createContext = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  return AudioCtx ? new AudioCtx() : null;
};

const now = (ctx) => (ctx ? ctx.currentTime : 0);

const createTone = (ctx, frequency, durationMs, type, volume) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = frequency;
  gain.gain.value = 0;
  osc.connect(gain);
  gain.connect(ctx.destination);

  const start = now(ctx);
  const duration = durationMs / 1000;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

  osc.start(start);
  osc.stop(start + duration + 0.02);
  osc.onended = () => {
    osc.disconnect();
    gain.disconnect();
  };
};

const createChord = (ctx, frequencies, durationMs, type, volume) => {
  frequencies.forEach((freq) => createTone(ctx, freq, durationMs, type, volume));
};

export function createSoundManager() {
  let ctx = null;
  let muted = false;
  let ambienceEnabled = false;
  let ambienceNodes = null;

  const ensureContext = async () => {
    if (!ctx) ctx = createContext();
    if (!ctx) return null;
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (err) {
        return ctx;
      }
    }
    return ctx;
  };

  const play = (fn) => {
    if (muted) return;
    ensureContext().then((audioCtx) => {
      if (!audioCtx) return;
      fn(audioCtx);
    });
  };

  const stopAmbience = () => {
    if (!ambienceNodes) return;
    ambienceNodes.osc.stop();
    ambienceNodes.osc.disconnect();
    ambienceNodes.gain.disconnect();
    ambienceNodes = null;
  };

  const startAmbience = () => {
    if (muted || !ambienceEnabled) return;
    ensureContext().then((audioCtx) => {
      if (!audioCtx || ambienceNodes) return;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 110;
      gain.gain.value = 0.015;
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start();
      ambienceNodes = { osc, gain };
    });
  };

  return {
    setMuted(value) {
      muted = value;
      if (muted) {
        stopAmbience();
      } else {
        startAmbience();
      }
    },
    setAmbienceEnabled(value) {
      ambienceEnabled = value;
      if (!ambienceEnabled) {
        stopAmbience();
        return;
      }
      startAmbience();
    },
    playCardPlace() {
      play((audioCtx) => createTone(audioCtx, 240, 80, 'square', DEFAULT_VOLUME * 0.7));
    },
    playDealTick() {
      play((audioCtx) => createTone(audioCtx, 280, 45, 'sine', DEFAULT_VOLUME * 0.32));
    },
    playCardPickup() {
      play((audioCtx) => createTone(audioCtx, 320, 90, 'triangle', DEFAULT_VOLUME * 0.8));
    },
    playSurg() {
      play((audioCtx) => createChord(audioCtx, [220, 330, 440], 220, 'sawtooth', DEFAULT_VOLUME));
    },
    playRoundEnd() {
      play((audioCtx) => createChord(audioCtx, [392, 494], 260, 'triangle', DEFAULT_VOLUME * 0.9));
    },
    playGameEnd() {
      play((audioCtx) => createChord(audioCtx, [262, 330, 392], 420, 'triangle', DEFAULT_VOLUME));
    },
    unlock() {
      ensureContext();
    },
  };
}
